import { useState, useEffect, useCallback } from 'react';
import { Category, Exam } from '../types';
import * as db from '../services/dataService';

interface UseExamDataReturn {
    categories: Category[];
    setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
    exams: Exam[];
    setExams: React.Dispatch<React.SetStateAction<Exam[]>>;
    isLoadingData: boolean;
    handleAddCategory: (category: Category) => Promise<void>;
    handleUpdateCategory: (updatedCategory: Category) => Promise<void>;
    handleDeleteCategory: (categoryId: string) => Promise<void>;
    handleAddExam: (exam: Exam) => Promise<void>;
    handleUpdateExam: (updatedExam: Exam) => Promise<void>;
    handleDeleteExam: (examId: string) => Promise<void>;
}

export const useExamData = (): UseExamDataReturn => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [exams, setExams] = useState<Exam[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Initial Data Fetch
    useEffect(() => {
        const initData = async () => {
            setIsLoadingData(true);
            try {
                const cats = await db.fetchCategories();
                const exms = await db.fetchExams();
                setCategories(cats);
                setExams(exms);
            } catch (e) {
                console.error("Failed to load data", e);
            } finally {
                setIsLoadingData(false);
            }
        };

        initData();
    }, []);

    const handleAddCategory = useCallback(async (category: Category) => {
        const saved = await db.saveCategory(category);
        setCategories(prev => [...prev, saved]);
    }, []);

    const handleUpdateCategory = useCallback(async (updatedCategory: Category) => {
        const saved = await db.saveCategory(updatedCategory);
        setCategories(prev => prev.map(c => c.id === saved.id ? saved : c));
    }, []);

    const handleAddExam = useCallback(async (exam: Exam) => {
        const saved = await db.saveExam(exam);
        setExams(prev => [...prev, saved]);
    }, []);

    const handleUpdateExam = useCallback(async (updatedExam: Exam) => {
        const saved = await db.saveExam(updatedExam);
        setExams(prev => prev.map(e => e.id === saved.id ? saved : e));
    }, []);

    const handleDeleteExam = useCallback(async (examId: string) => {
        await db.deleteExam(examId);
        setExams(prev => prev.filter(e => e.id !== examId));
    }, []);

    const handleDeleteCategory = useCallback(async (categoryId: string) => {
        await db.deleteCategory(categoryId);
        setCategories(prev => prev.filter(c => c.id !== categoryId));
    }, []);

    return {
        categories,
        setCategories,
        exams,
        setExams,
        isLoadingData,
        handleAddCategory,
        handleUpdateCategory,
        handleDeleteCategory,
        handleAddExam,
        handleUpdateExam,
        handleDeleteExam
    };
};

export default useExamData;
